/** The case editor's fields: who the client is (internal) and how it is shown, where and how far along it is. */

import { useState } from 'react';
import { X } from 'lucide-react';

import {
  caseCreateInAchievementsOneMax,
  caseCreateInAliasesOneItemMax,
  caseCreateInAnonymizedLabelMax,
  caseCreateInApproachOneMax,
  caseCreateInClientNameOneMax,
  caseCreateInObjectiveOneMax,
  caseCreateInSituationOneMax,
} from '@/api/generated/schemas';
import { useCountries } from '@/api/queries/countries';
import { useIndustries } from '@/api/queries/industries';
import { CASE_STATUSES, CASE_TEXT_FIELDS, CASE_TYPES, type CaseStatus, type CaseTextKey, type CaseType } from '@/api/types/cases';
import type { FieldErrors } from '@/api/validate';
import { FieldError, fieldA11y } from '@/ui/FieldError';

import type { CaseForm } from './caseForm';

const TEXT_MAX: Record<CaseTextKey, number> = {
  situation: caseCreateInSituationOneMax,
  objective: caseCreateInObjectiveOneMax,
  approach: caseCreateInApproachOneMax,
  achievements: caseCreateInAchievementsOneMax,
};

/** The identity fields that show a problem beside them, by path, with their inputs' ids. */
const IDENTITY_IDS = { client_name: 'case-client', anonymized_label: 'case-label', aliases: 'case-alias' } as const satisfies Partial<Record<keyof CaseForm, string>>;

/** Every path shown beside its field here; the editor's banner says the rest (a metric's row there may differ). */
export const FIELD_PATHS: string[] = [...Object.keys(IDENTITY_IDS), ...CASE_TEXT_FIELDS.map(({ key }) => key)];

export type SetField = <K extends keyof CaseForm>(key: K, value: CaseForm[K]) => void;

function AliasInput({ aliases, onChange, error }: { aliases: string[]; onChange: (next: string[]) => void; error?: string }) {
  const [entry, setEntry] = useState('');
  function add() {
    const alias = entry.trim();
    if (alias && !aliases.some((a) => a.toLowerCase() === alias.toLowerCase())) onChange([...aliases, alias]);
    setEntry('');
  }
  return (
    <div className="field">
      <label htmlFor={IDENTITY_IDS.aliases}>Aliases</label>
      <div className="alias-entry">
        <input
          id={IDENTITY_IDS.aliases}
          type="text"
          maxLength={caseCreateInAliasesOneItemMax}
          value={entry}
          {...fieldA11y(IDENTITY_IDS.aliases, error, 'case-alias-hint')}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={add} disabled={!entry.trim()}>Add alias</button>
      </div>
      <span className="hint" id="case-alias-hint">Abbreviations, trade names and subsidiaries; the anonymized version replaces them too.</span>
      <FieldError inputId={IDENTITY_IDS.aliases} message={error} />
      {aliases.length > 0 && (
        <ul className="alias-chips" aria-label="Aliases added">
          {aliases.map((a) => (
            <li key={a} className="badge badge-chip">
              {a}
              <button type="button" aria-label={`Remove alias ${a}`} onClick={() => onChange(aliases.filter((x) => x !== a))}>
                <X aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Select({ id, label, value, onChange, options, empty }: { id: string; label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; empty?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {empty !== undefined && <option value="">{empty}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function IdentityFields({ form, set, errors }: { form: CaseForm; set: SetField; errors: FieldErrors }) {
  const industries = useIndustries();
  const countries = useCountries();
  const industryOptions = (industries.data?.industries ?? [])
    .filter((i) => (i.status === 'approved' && i.is_active) || i.id === form.industry_id)
    .map((i) => ({ value: i.id, label: i.name }));
  const countryOptions = (countries.data ?? []).map((c) => ({ value: c.code, label: c.name }));
  return (
    <div className="grid grid-2">
      <Select id="case-type" label="Type" value={form.type} onChange={(v) => set('type', v as CaseType)} options={CASE_TYPES} />
      <Select id="case-status" label="Status" value={form.status} onChange={(v) => set('status', v as CaseStatus)} options={CASE_STATUSES} />
      <div className="field">
        <label htmlFor={IDENTITY_IDS.client_name}>Client name</label>
        <input id={IDENTITY_IDS.client_name} type="text" maxLength={caseCreateInClientNameOneMax} value={form.client_name} {...fieldA11y(IDENTITY_IDS.client_name, errors.client_name, 'case-client-hint')} onChange={(e) => set('client_name', e.target.value)} />
        <span className="hint" id="case-client-hint">Internal: only people who may read internal content see it.</span>
        <FieldError inputId={IDENTITY_IDS.client_name} message={errors.client_name} />
      </div>
      <div className="field">
        <label htmlFor={IDENTITY_IDS.anonymized_label}>Anonymized label</label>
        <input id={IDENTITY_IDS.anonymized_label} type="text" required maxLength={caseCreateInAnonymizedLabelMax} value={form.anonymized_label} {...fieldA11y(IDENTITY_IDS.anonymized_label, errors.anonymized_label, 'case-label-hint')} onChange={(e) => set('anonymized_label', e.target.value)} />
        <span className="hint" id="case-label-hint">How clients read it, such as &quot;Tier 1 bank&quot;.</span>
        <FieldError inputId={IDENTITY_IDS.anonymized_label} message={errors.anonymized_label} />
      </div>
      <div className="field span-2">
        <AliasInput aliases={form.aliases} onChange={(next) => set('aliases', next)} error={errors.aliases ?? Object.entries(errors).find(([k]) => k.startsWith('aliases.'))?.[1]} />
      </div>
      <Select id="case-industry" label="Industry" value={form.industry_id} onChange={(v) => set('industry_id', v)} options={industryOptions} empty="No industry" />
      <Select id="case-country" label="Country" value={form.country} onChange={(v) => set('country', v)} options={countryOptions} empty="No country" />
    </div>
  );
}

export function TextFields({ form, set, errors }: { form: CaseForm; set: SetField; errors: FieldErrors }) {
  return (
    <>
      {CASE_TEXT_FIELDS.map(({ key, label }) => (
        <div key={key} className="field">
          <label htmlFor={`case-${key}`}>{label}</label>
          <textarea id={`case-${key}`} rows={3} maxLength={TEXT_MAX[key]} value={form[key]} {...fieldA11y(`case-${key}`, errors[key])} onChange={(e) => set(key, e.target.value)} />
          <FieldError inputId={`case-${key}`} message={errors[key]} />
        </div>
      ))}
    </>
  );
}
